import { BRAND_CHANNELS } from '../utils/scoring';

const DIMENSIONS = ['Tone', 'Visual Identity', 'Key Message', 'Value Proposition', 'Personality'];

export default function BrandCoherenceMap({ data, onChange }) {
  const grid = data?._grid || {};

  // If AI provided text-based coherence data, show it above the grid
  const aiNotes = {
    website: data?.websiteAlign || '',
    menu: data?.menuAlign || '',
    social: data?.socialAlign || '',
    reviews: data?.reviewAlign || '',
    overall: data?.overallNotes || '',
  };
  const hasAINotes = Object.values(aiNotes).some(v => v);

  const setCell = (channelId, dim, val) => {
    const newGrid = { ...grid };
    if (!newGrid[channelId]) newGrid[channelId] = {};
    newGrid[channelId][dim] = val;
    onChange({ ...data, _grid: newGrid });
  };

  const getCell = (channelId, dim) => {
    return grid[channelId]?.[dim] ?? 'neutral';
  };

  // Different colors with different score weights
  const colorMap = {
    aligned: { bg: 'bg-emerald-500/40', label: 'Aligned', weight: 1.0 },
    partial: { bg: 'bg-amber-500/40', label: 'Partial', weight: 0.5 },
    fractured: { bg: 'bg-red-500/30', label: 'Fractured', weight: 0.0 },
    neutral: { bg: 'bg-neutral-700/30', label: 'Not Scored', weight: null },
  };

  // Calculate coherence score — only count scored cells
  let totalWeight = 0;
  let scoredCells = 0;
  BRAND_CHANNELS.forEach(ch => {
    DIMENSIONS.forEach(dim => {
      const val = getCell(ch.id, dim);
      if (val !== 'neutral' && colorMap[val]?.weight !== null) {
        totalWeight += colorMap[val].weight;
        scoredCells++;
      }
    });
  });
  const coherenceScore = scoredCells > 0 ? Math.round((totalWeight / scoredCells) * 100) : 0;
  const scoreColor = coherenceScore >= 75 ? 'text-emerald-400' : coherenceScore >= 50 ? 'text-amber-400' : coherenceScore > 0 ? 'text-red-400' : 'text-neutral-500';

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display text-lg text-white">Brand Coherence Heat Map</h3>
          <p className="text-xs text-neutral-500 mt-0.5">Alignment across touchpoints and dimensions</p>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-black ${scoreColor}`}>{scoredCells > 0 ? `${coherenceScore}%` : '—'}</p>
          <p className="text-xs text-neutral-500">Coherence</p>
        </div>
      </div>

      {/* AI Notes */}
      {hasAINotes && (
        <div className="bg-neutral-800/30 rounded-lg p-4 mb-5 space-y-2">
          <p className="text-xs text-brand-gold font-semibold uppercase tracking-wider mb-2">AI Analysis</p>
          {aiNotes.website && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Website:</span> {aiNotes.website}</p>}
          {aiNotes.menu && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Menu:</span> {aiNotes.menu}</p>}
          {aiNotes.social && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Social:</span> {aiNotes.social}</p>}
          {aiNotes.reviews && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Reviews:</span> {aiNotes.reviews}</p>}
          {aiNotes.overall && <p className="text-xs text-neutral-300 mt-2 pt-2 border-t border-neutral-700/50">{aiNotes.overall}</p>}
        </div>
      )}

      {/* Legend */}
      <div className="flex gap-4 mb-4 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/40" /> Aligned (100%)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/40" /> Partial (50%)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500/30" /> Fractured (0%)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-neutral-700/30" /> Not Scored</span>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left text-neutral-500 font-medium pb-2 pr-3"></th>
              {DIMENSIONS.map(d => (
                <th key={d} className="text-center text-neutral-500 font-medium pb-2 px-1 whitespace-nowrap">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BRAND_CHANNELS.map(ch => (
              <tr key={ch.id}>
                <td className="text-neutral-300 font-medium py-1.5 pr-3 whitespace-nowrap">{ch.name}</td>
                {DIMENSIONS.map(dim => {
                  const val = getCell(ch.id, dim);
                  return (
                    <td key={dim} className="text-center py-1.5 px-1">
                      <button
                        onClick={() => {
                          const cycle = { neutral: 'aligned', aligned: 'partial', partial: 'fractured', fractured: 'neutral' };
                          setCell(ch.id, dim, cycle[val]);
                        }}
                        className={`w-full h-8 rounded ${colorMap[val].bg} hover:opacity-80 transition-all`}
                        title={`${ch.name} / ${dim}: ${colorMap[val].label}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-neutral-600 mt-3">Click cells to cycle: Not Scored → Aligned → Partial → Fractured</p>

      {/* Team Analysis */}
      <div className="mt-5 border-t border-neutral-800 pt-4">
        <p className="text-xs text-brand-gold font-semibold mb-2 uppercase tracking-wider">Team Analysis</p>
        <textarea
          value={data?.teamAnalysis || ''}
          onChange={(e) => onChange({ ...data, teamAnalysis: e.target.value })}
          placeholder="Add your team's brand coherence analysis — where is the brand consistent, where is it fractured, what needs to change..."
          rows={4}
          className="input-field text-sm"
        />
      </div>
    </div>
  );
}
